/**
 * Static (SSR) mobile bottom nav for the SRP shell.
 * Renders the same layout as MobileBottomNav (search placeholder, All Filters button, menu)
 * so the initial paint on mobile shows full chrome; no interactivity (no Autocomplete, no modals).
 */
import { List, Funnel, MagnifyingGlass } from '@phosphor-icons/react';
import type { SrpStaticStrings } from '../../../features/srp/SrpViewModel';

const iosFontStack =
  "font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif]";

export interface StaticMobileBottomNavProps {
  staticStrings: SrpStaticStrings;
}

/**
 * Renders a non-interactive mobile bottom bar that matches MobileBottomNav layout and styling.
 * Used in the static SRP shell so mobile viewports see the full nav on first paint.
 */
export function StaticMobileBottomNav({ staticStrings }: StaticMobileBottomNavProps): React.ReactElement {
  return (
    <nav
      className={`w-full z-[60] flex items-center gap-2 px-3 py-2 min-h-14 border-t border-[#DDD5C8]/80 dark:border-[#4A3520] lg:hidden bg-white/95 dark:bg-[#1F1509]/95 shadow-lg dark:shadow-[0_-2px_12px_rgba(0,0,0,0.35)] rounded-t-2xl transition-colors duration-300 ${iosFontStack} max-w-full overflow-hidden flex-shrink-0`}
      style={{
        boxShadow: '0 -4px 24px 0 rgba(0,0,0,0.08)',
        paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))',
      }}
      aria-label="Mobile bottom navigation"
    >
      {/* Search bar placeholder – same flex and min-width as real Autocomplete container */}
      <div className="flex-1 min-w-0" aria-hidden>
        <div className="relative w-full z-[100]" data-component="static-mobile-autocomplete">
          <MagnifyingGlass
            weight="fill"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A6E5F] dark:text-[#8A7060] pointer-events-none z-10"
          />
          <div
            className="pl-11 pr-10 py-3 w-full text-base bg-white/90 dark:bg-[#1F1509]/90 border border-[#DDD5C8]/70 dark:border-[#4A3520]/70 rounded-3xl text-[#7A6E5F] dark:text-[#8A7060] font-medium overflow-hidden whitespace-nowrap text-ellipsis"
            role="presentation"
          >
            {staticStrings.searchPlaceholder}
          </div>
          <span
            className="absolute right-3 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-full bg-[#F2EADF] dark:bg-[#2A1D0F] text-[#5A4A3A] dark:text-[#C4A882] text-xs font-mono font-semibold select-none pointer-events-none border border-[#DDD5C8] dark:border-[#4A3520] backdrop-blur-sm shadow-sm dark:shadow-none opacity-90"
            aria-hidden
          >
            /
          </span>
        </div>
      </div>
      {/* All Filters button – outline funnel, same size as real (static placeholder) */}
      <div
        className="flex items-center justify-center flex-none rounded-full p-3 font-bold border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#2A1D0F] shadow-sm dark:shadow-none"
        aria-label={staticStrings.allFiltersAria}
        role="presentation"
      >
        <Funnel className="w-6 h-6 text-[#7A6E5F] dark:text-[#98CFA8]" />
      </div>
      {/* Menu button – same size as real (static placeholder) */}
      <div
        className="flex items-center justify-center flex-none rounded-full p-3 font-bold border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#2A1D0F] shadow-sm dark:shadow-none"
        aria-label={staticStrings.menuLabel}
        role="presentation"
      >
        <List className="w-6 h-6 text-[#3D3529] dark:text-[#C4A882]" />
      </div>
    </nav>
  );
}
